import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Alert, Button } from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { makeInputNumeric, TextField } from 'shared/ui/TextField'
import { type ManageNumbersStore } from 'pages/settings/pages/numbers/ui/ManageNumbers/store/ManageNumbersStore'
import styles from './styles.module.scss'

type IManageNumbersModalProps = {
  manageNumbersStore: ManageNumbersStore
}

export const RemoveNumbersModal = observer(({ manageNumbersStore }: IManageNumbersModalProps) => {
  const {
    licensesCount,
    onLicenseRemoveBack,
    removeLicenseNumber,
    onRemoveLicenseNumberChange,
    onLicenseRemove,
    priceInfoUiData,
    changeNumberStatus,
  } = manageNumbersStore
  const removeNumberIndication = Number.isNaN(removeLicenseNumber) ? 1 : removeLicenseNumber

  const onBlur = () => {
    if (Number.isNaN(removeLicenseNumber)) {
      onRemoveLicenseNumberChange(1)
    }
  }

  const onChange = (value: string) => {
    const count = parseInt(value)
    const newValue = count > licensesCount ? licensesCount : count
    if (newValue !== removeLicenseNumber) {
      onRemoveLicenseNumberChange(count > licensesCount ? licensesCount : count)
    }
  }

  const NumericComponent = useMemo(
    () =>
      makeInputNumeric({
        allowNegative: false,
        decimalScale: 0,
        isAllowed: ({ value }) => {
          const count = parseInt(value)
          return Number.isNaN(count) || (count > 0 && count <= licensesCount)
        },
      }),
    [licensesCount]
  )

  return (
    <div className={styles.root}>
      <div className={styles.notification}>
        <Alert
          item={{
            type: 'warning',
            title: `Your workspace has ${licensesCount} empty license${
              licensesCount > 1 ? 's' : ''
            } available for removal`,
            desc: (
              <div>
                <div>The license will be removed at the end of the billing cycle:</div>
                <div>{priceInfoUiData?.nextChargeDate}</div>
              </div>
            ),
          }}
        />
        {changeNumberStatus.isFailed && (
          <Alert
            item={{
              type: 'error',
              desc: changeNumberStatus.error,
            }}
          />
        )}
      </div>
      <div>
        <Label
          label={{
            text: 'Number of number licenses you want to remove',
          }}
        />
        <TextField
          Input={NumericComponent}
          size={'large'}
          onChange={onChange}
          onBlur={onBlur}
          variant={'stroke'}
          value={String(removeLicenseNumber)}
          InputProps={{
            className: styles.search,
          }}
        />
      </div>

      <div className={styles.actions}>
        <Button
          typeBtn={'contained'}
          text={'Back'}
          size={'medium'}
          contained={'secondary'}
          textClassname={styles.button}
          onClick={onLicenseRemoveBack}
        />
        <Button
          disabled={changeNumberStatus.isLoading}
          typeBtn={'contained'}
          text={`Remove ${removeNumberIndication} number license${
            removeNumberIndication > 1 ? 's' : ''
          }`}
          size={'medium'}
          textClassname={styles.button}
          onClick={onLicenseRemove}
        />
      </div>
    </div>
  )
})
